import { RootState } from '@/store';
import { ActionContext, ActionTree } from 'vuex';
import { Mutations, MutationTypes } from './mutations';
import { State } from './state';
import ApiService from '@/services/ApiService';

import { Actions as CommonActions, ActionTypes as CommonActionTypes } from '../Common/actions';

import { Signatur, Vertragspartner } from '@/models/VertragspartnerModels';

export enum ActionTypes {
  GetVertragspartner = 'GETVERTRAGSPARTNER',
  CreateVertragspartner = 'CREATEVERTRAGSPARTNER',
  UpdateVertragspartner = 'UPDATEVERTRAGSPARTNER',
  DeleteVertragspartner = 'DELETEVERTRAGSPARTNER',

  GetSignaturen = 'GETSIGNATUREN',
  CreateSignatur = 'CREATESIGNATUR',
  UpdateSignatur = 'UPDATESIGNATUR',
  DeleteSignatur = 'DELETESIGNATUR',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload?: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetVertragspartner](context: ActionArguments): Promise<void>;
  [ActionTypes.CreateVertragspartner](context: ActionArguments, data: Vertragspartner): Promise<void>;
  [ActionTypes.UpdateVertragspartner](context: ActionArguments, data: Vertragspartner): Promise<void>;
  [ActionTypes.DeleteVertragspartner](context: ActionArguments, vertragspartnerid: number): Promise<void>;

  [ActionTypes.GetSignaturen](context: ActionArguments): Promise<void>;
  [ActionTypes.CreateSignatur](context: ActionArguments, data: Signatur): Promise<void>;
  [ActionTypes.UpdateSignatur](context: ActionArguments, data: Signatur): Promise<void>;
  [ActionTypes.DeleteSignatur](context: ActionArguments, signaturid: number): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  //#region Vertragspartner
  async [ActionTypes.GetVertragspartner]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getVertragspartnerLoading = true;
    status.vertragspartnernErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetVertragspartner, new Array<Vertragspartner>());

    return ApiService.getAllVertragspartner()
      .then((res) => {
        const data: Array<Vertragspartner> = res.data;
        commit(MutationTypes.SetVertragspartner, data);
      })
      .catch((error) => {
        status.vertragspartnernErrorMsg = error.response.data.detail;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getVertragspartnerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateVertragspartner]({ commit, dispatch, rootGetters }: ActionArguments, data: Vertragspartner) {
    const status = rootGetters.status;
    status.createVertragspartnerLoading = true;
    status.vertragspartnernErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createVertragspartner(data)
      .then((res) => {
        const data: Vertragspartner = res.data;
        commit(MutationTypes.AddVertragspartner, data);
      })
      .catch((error) => {
        console.log(error);
        status.vertragspartnernErrorMsg = error.response;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.createVertragspartnerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateVertragspartner]({ commit, dispatch, rootGetters }: ActionArguments, data: Vertragspartner) {
    const status = rootGetters.status;
    status.updateVertragspartnerLoading = true;
    status.vertragspartnernErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateVertragspartner(data)
      .then((res) => {
        const data: Vertragspartner = res.data;
        commit(MutationTypes.AddOrUpdateVertragspartner, data);
      })
      .catch((error) => {
        status.vertragspartnernErrorMsg = error.response;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateVertragspartnerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteVertragspartner]({ commit, dispatch, rootGetters }: ActionArguments, vertragspartnerid: number) {
    const status = rootGetters.status;
    status.deleteVertragspartnerLoading = true;
    status.vertragspartnernErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteVertragspartner(vertragspartnerid)
      .then((res) => {
        commit(MutationTypes.DeleteVertragspartner, vertragspartnerid);
      })
      .catch((error) => {
        console.log(error);
        status.vertragspartnernErrorMsg = error.response;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.deleteVertragspartnerLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion

  //#region Signatur
  async [ActionTypes.GetSignaturen]({ commit, dispatch, rootGetters }: ActionArguments) {
    const status = rootGetters.status;
    status.getSignaturenLoading = true;
    status.signaturErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    commit(MutationTypes.SetSignatur, new Array<Signatur>());

    return ApiService.getAllSignaturen()
      .then((res) => {
        const data: Array<Signatur> = res.data;
        commit(MutationTypes.SetSignatur, data);
      })
      .catch((error) => {
        status.signaturErrorMsg = error.response.data.detail;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.getSignaturenLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.CreateSignatur]({ commit, dispatch, rootGetters }: ActionArguments, data: Signatur) {
    const status = rootGetters.status;
    status.createSignaturLoading = true;
    status.signaturErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.createSignatur(data)
      .then((res) => {
        const data: Signatur = res.data;
        commit(MutationTypes.AddSignatur, data);
      })
      .catch((error) => {
        console.log(error);
        status.signaturErrorMsg = error.response;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.createSignaturLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.UpdateSignatur]({ commit, dispatch, rootGetters }: ActionArguments, data: Signatur) {
    const status = rootGetters.status;
    status.updateSignaturLoading = true;
    status.signaturErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.updateSignatur(data)
      .then((res) => {
        const data: Signatur = res.data;
        commit(MutationTypes.AddOrUpdateSignatur, data);
      })
      .catch((error) => {
        status.signaturErrorMsg = error.response;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.updateSignaturLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },

  async [ActionTypes.DeleteSignatur]({ commit, dispatch, rootGetters }: ActionArguments, signaturid: number) {
    const status = rootGetters.status;
    status.deleteSignaturLoading = true;
    status.signaturErrorMsg = '';
    dispatch(CommonActionTypes.SetStatus, status);

    return ApiService.deleteSignatur(signaturid)
      .then((res) => {
        commit(MutationTypes.DeleteSignatur, signaturid);
      })
      .catch((error) => {
        console.log(error);
        status.signaturErrorMsg = error.response;
        dispatch(CommonActionTypes.SetStatus, status);
      })
      .finally(() => {
        status.deleteSignaturLoading = false;
        dispatch(CommonActionTypes.SetStatus, status);
      });
  },
  //#endregion
};
